import type { PlainEnvironment } from "@/queries/environment-queries";
import type { PlainProject } from "@/queries/project-queries";
import type { EffectiveState } from "@/routes/environments/phase";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import {
    EnvironmentPhase,
    type Environment,
    type EnvironmentInitializer,
} from "gitpod-next-api/gitpod/v1/environment_pb";

export function statusText(state?: EffectiveState): string | undefined {
    if (!state) {
        return;
    }

    let text = "Unknown";

    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            text = "Running";
            break;
        case EnvironmentPhase.CREATING:
            text = "Starting";
            break;
        case EnvironmentPhase.STARTING:
            if (state.resuming) {
                text = "Resuming";
            } else {
                text = "Starting";
            }
            break;
        case EnvironmentPhase.UPDATING:
            text = "Updating";
            break;
        case EnvironmentPhase.STOPPING:
            text = "Stopping";
            if (state.timeout) {
                text = `Auto-stopping`;
            }
            break;
        case EnvironmentPhase.STOPPED:
            text = "Stopped";
            if (state.timeout) {
                text = `Auto-stopped`;
            }
            break;
        case EnvironmentPhase.DELETING:
            text = "Deleting";
            break;
        case EnvironmentPhase.DELETED:
            text = "Deleted";
            break;
    }

    return text;
}

export function environmentLogsUrl(environment?: PlainEnvironment) {
    if (!environment?.status?.environmentUrls?.logs) {
        return;
    }
    // We're adding the status machine session to the URL to trigger a refresh when a new machine session starts
    // The macOS runner resets the log stream, so this ensures we clear our logs as well.
    return `${environment?.status?.environmentUrls?.logs}#${environment?.status?.machine?.session}`;
}

export function environmentLogGroupUrl(environmentId?: string, logGroupId?: string) {
    return `/details/${environmentId}/logs${logGroupId ? `#${logGroupId}` : ""}`;
}

/**
 * Extracts the git branch from the environment initializer.
 *
 * The environment initializer can contain multiple initialization specs, each with a different type of context.
 * This function searches through the specs and returns the git branch from the first spec that has a "contextUrl" or "git" case.
 *
 * @param initializer The environment initializer object.
 * @returns The repository URL, or undefined if no URL could be found.
 */
export function getBranchFromInitializer(initializer: PlainMessage<EnvironmentInitializer> | undefined) {
    if (!initializer?.specs || initializer.specs.length === 0) {
        return "main"; // default branch for empty initializer
    }
    const urls = initializer?.specs
        .map((s) => {
            switch (s.spec.case) {
                case "contextUrl":
                    return extractBranchFromUrl(s.spec.value.url);
                case "git":
                    return s.spec.value.cloneTarget;
            }
        })
        .filter((u) => !!u)
        .map((u) => u!);
    return urls?.[0];
}

export function extractBranchFromUrl(url: string) {
    // This works for both GitHub and GitLab URLs
    // Might need to be adjusted for future SCM providers
    const parts = url.split("/tree/");

    // We're defaulting to "default branch" here instead of "main" as we can't know what the default branch is for the repository.
    if (parts.length === 1) {
        return "default branch";
    }

    const branch = parts.pop()?.replace(/\?.*/, "");
    return branch || url;
}

export function isBlankEnvironment(environment: PlainMessage<Environment> | undefined) {
    return environment?.spec?.content?.initializer?.specs?.length === 0;
}

export type GroupedArchivedEnvironments = {
    oneToTwoWeeks: ArchivedEnvironment[];
    twoWeeksToMonth: ArchivedEnvironment[];
    olderThanMonth: ArchivedEnvironment[];
};

export const groupArchivedEnvironmentsByArchiveDate = (
    environments: ArchivedEnvironment[],
): GroupedArchivedEnvironments => {
    const groupedEnvironments: GroupedArchivedEnvironments = {
        oneToTwoWeeks: [],
        twoWeeksToMonth: [],
        olderThanMonth: [],
    };

    if (environments.length > 0) {
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        oneWeekAgo.setMilliseconds(0);
        const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        twoWeeksAgo.setMilliseconds(0);
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        oneMonthAgo.setMilliseconds(0);

        environments.forEach((env) => {
            const archivedAt = env.environment.metadata?.archivedAt
                ? new Timestamp(env.environment.metadata?.archivedAt).toDate()
                : undefined;
            if (!archivedAt) return;

            archivedAt.setMilliseconds(0);

            if (archivedAt > oneWeekAgo) {
                return;
            }
            if (archivedAt <= oneWeekAgo && archivedAt > twoWeeksAgo) {
                groupedEnvironments.oneToTwoWeeks.push(env);
            } else if (archivedAt <= twoWeeksAgo && archivedAt > oneMonthAgo) {
                groupedEnvironments.twoWeeksToMonth.push(env);
            } else if (archivedAt <= oneMonthAgo) {
                groupedEnvironments.olderThanMonth.push(env);
            }
        });

        const sortByArchivedAt = (a: ArchivedEnvironment, b: ArchivedEnvironment) => {
            const aTime = new Timestamp(a.environment.metadata?.archivedAt).toDate().getTime();
            const bTime = new Timestamp(b.environment.metadata?.archivedAt).toDate().getTime();
            return bTime - aTime;
        };

        groupedEnvironments.oneToTwoWeeks.sort(sortByArchivedAt);
        groupedEnvironments.twoWeeksToMonth.sort(sortByArchivedAt);
        groupedEnvironments.olderThanMonth.sort(sortByArchivedAt);
    }

    return groupedEnvironments;
};

export type ArchivedEnvironment = {
    environment: PlainEnvironment;
    project?: PlainProject;
    projectMissing?: boolean;
    repoFullName?: string;
};

export function environmentsByProjectsToArchivedEnvironments(
    environmentsByProjects: {
        environments: PlainEnvironment[];
        project?: PlainProject;
        projectMissing?: boolean;
        repoFullName?: string;
    }[],
): ArchivedEnvironment[] {
    const archivedEnvironments: ArchivedEnvironment[] = [];

    environmentsByProjects.forEach((group) => {
        group.environments.forEach((env) => {
            archivedEnvironments.push({
                environment: env,
                project: group.project,
                projectMissing: group.projectMissing,
                repoFullName: group.repoFullName,
            });
        });
    });

    return archivedEnvironments;
}
